import React, { useEffect, useState, useMemo } from 'react';
import { Table, Form, Button, Modal, Spinner, Badge, InputGroup, Row, Col, Card, Pagination } from 'react-bootstrap';
import axios from 'axios';
import { 
  FaSearch, 
  FaEye, 
  FaUserCircle, 
  FaBriefcase, 
  FaClock, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaFilter,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUndo
} from 'react-icons/fa';
import moment from 'moment';
import { toast } from 'react-toastify';

const JobApplications = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [userProfileLoading, setUserProfileLoading] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter States
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const appsPerPage = 5;

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await axios.get('https://api.collegeall.in/api/v1/all-applications');
      const apps = res.data.applications || [];
      setApplications(apps);
      setFilteredApplications(apps);
    } catch (err) {
      console.error('Error fetching applications', err);
      toast.error('Failed to sync with application database.');
    } finally {
      setLoading(false);
    }
  };

  // Derive unique categories for the filter dropdown
  const categoriesList = useMemo(() => {
    const unique = [...new Set(applications.map(app => app.job_category).filter(Boolean))];
    return unique.sort();
  }, [applications]);

  // Combined Filtering Logic
  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = applications.filter(app => {
      const matchesSearch = app.title?.toLowerCase().includes(term) ||
                            app.full_name?.toLowerCase().includes(term) ||
                            app.company_name?.toLowerCase().includes(term);
      const matchesStatus = !filterStatus || app.job_status === filterStatus;
      const matchesCategory = !filterCategory || app.job_category === filterCategory;
      
      return matchesSearch && matchesStatus && matchesCategory;
    });
    setFilteredApplications(filtered);
    setCurrentPage(1);
  }, [searchTerm, applications, filterStatus, filterCategory]);

  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('');
    setFilterCategory('');
  };

  const handleStatusChange = async (applicationId, status) => {
    try {
      await axios.put(`https://api.collegeall.in/api/v1/update-application-status/${applicationId}`, { status });
      fetchApplications();
      toast.success(`Application moved to ${status} stage.`);
    } catch (err) {
      console.error('Status update failed', err);
      toast.error('Workflow update failed. Please try again.');
    }
  };

  const renderProfileImage = (user, size = 40) => {
    if (!user) return null;
    const name = user.full_name || 'U';
    return user.profile_pic ? (
      <img
        src={`https://api.collegeall.in/api/v1/uploads/${user.profile_pic}`}
        alt="Profile"
        className="rounded-circle border shadow-sm"
        style={{ width: size, height: size, objectFit: 'cover' }}
        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
      />
    ) : null;
  };

  const renderAvatarPlaceholder = (name, size = 40) => (
    <div
      className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold shadow-sm"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {name.charAt(0).toUpperCase()}
    </div>
  );

  const fetchUserProfile = async (email) => {
    setUserProfileLoading(true);
    setUserProfile(null);
    try {
      const response = await axios.get(`https://api.collegeall.in/api/v1/get-profile?email=${email}`);
      setUserProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.warn('Could not retrieve full profile details.');
    } finally {
      setUserProfileLoading(false);
    }
  };

  const handleProfileClick = async (app) => {
    setShowProfileModal(true);
    await fetchUserProfile(app.email);
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const indexOfLastApp = currentPage * appsPerPage;
  const indexOfFirstApp = indexOfLastApp - appsPerPage;
  const currentApps = filteredApplications.slice(indexOfFirstApp, indexOfLastApp);
  const totalPages = Math.ceil(filteredApplications.length / appsPerPage);

  const renderPaginationItems = () => {
    const items = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
            {i}
          </Pagination.Item>
        );
      }
      return items;
    }

    let start = currentPage - 1;
    let end = currentPage + 1;

    if (start <= 1) {
      start = 1;
      end = 3;
    } else if (end >= totalPages) {
      end = totalPages;
      start = totalPages - 2;
    }

    if (start > 1) {
      items.push(<Pagination.Ellipsis key="start-el" disabled />);
    }

    for (let i = start; i <= end; i++) {
      if (i < totalPages) {
        items.push(
          <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
            {i}
          </Pagination.Item>
        );
      }
    }

    if (end < totalPages - 1) {
      items.push(<Pagination.Ellipsis key="end-el" disabled />);
    }

    items.push(
      <Pagination.Item key={totalPages} active={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
        {totalPages}
      </Pagination.Item>
    );

    return items;
  };

  return (
    <div className="apps-mgmt-container p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">Application Pipeline</h2>
          <p className="text-muted small mb-0">Track and manage candidate progress across all listings</p>
        </div>
        <div className="d-flex gap-2">
          <Badge bg="white" text="dark" className="border shadow-sm d-flex align-items-center px-3 rounded-pill">
            <FaClock className="me-2 text-warning" /> {applications.filter(a => a.job_status === 'Pending').length} PENDING
          </Badge>
          <Badge bg="white" text="dark" className="border shadow-sm d-flex align-items-center px-3 rounded-pill">
            <FaCheckCircle className="me-2 text-success" /> {applications.filter(a => a.job_status === 'Accepted').length} HIRED
          </Badge>
        </div>
      </div>

      {/* Stats Cards */}
      <Row className="g-3 mb-4">
        {[
          { title: 'Total Apps', count: applications.length, icon: <FaBriefcase />, color: '#6366f1' },
          { title: 'In Review', count: applications.filter(a => a.job_status === 'Review').length, icon: <FaEye />, color: '#f59e0b' },
          { title: 'Accepted', count: applications.filter(a => a.job_status === 'Accepted').length, icon: <FaCheckCircle />, color: '#10b981' },
          { title: 'Rejected', count: applications.filter(a => a.job_status === 'Rejected').length, icon: <FaTimesCircle />, color: '#ef4444' },
        ].map((stat, i) => (
          <Col md={3} key={i}>
            <Card className="border-0 shadow-sm metric-card p-3">
              <div className="d-flex align-items-center">
                <div className="metric-icon" style={{ color: stat.color, background: `${stat.color}15` }}>
                  {stat.icon}
                </div>
                <div className="ms-3">
                  <div className="small text-muted fw-bold text-uppercase" style={{ fontSize: '0.65rem' }}>{stat.title}</div>
                  <h4 className="fw-bold mb-0">{stat.count}</h4>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
        <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center">
          <h6 className="fw-bold mb-0">Active Submissions</h6>
          <div className="d-flex gap-2">
            <InputGroup className="search-group" style={{ maxWidth: '280px' }}>
              <InputGroup.Text className="bg-light border-0">
                <FaSearch className="text-muted small" />
              </InputGroup.Text>
              <Form.Control
                className="bg-light border-0 small shadow-none"
                placeholder="Search candidates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Button 
              variant={showFilters ? "primary" : "light"} 
              className={`border shadow-none btn-icon ${showFilters ? 'text-white' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter />
            </Button>
          </div>
        </div>

        {/* Filter Bar */}
        {showFilters && (
          <div className="px-3 py-2 bg-light-subtle border-bottom animate-fade-in">
            <Row className="g-2 align-items-center">
              <Col md={3}>
                <Form.Select 
                  size="sm" 
                  className="border-0 shadow-sm x-small"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="Pending">Pending</option>
                  <option value="Review">Review</option>
                  <option value="Accepted">Accepted</option>
                  <option value="Rejected">Rejected</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select 
                  size="sm" 
                  className="border-0 shadow-sm x-small"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categoriesList.map((cat, i) => (
                    <option key={i} value={cat}>{cat}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Button variant="link" className="text-muted x-small p-0 text-decoration-none d-flex align-items-center gap-1" onClick={resetFilters}>
                  <FaUndo size={8} /> Reset Filters
                </Button>
              </Col>
            </Row>
          </div>
        )}

        <div className="table-responsive">
          <Table hover className="modern-table mb-0 align-middle">
            <thead>
              <tr>
                <th className="ps-4">Candidate</th>
                <th>Applied For</th>
                <th>Company</th>
                <th>Applied Date</th>
                <th>Workflow Status</th>
                <th className="text-end pe-4">Command</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-5"><Spinner animation="border" size="sm" variant="primary" /></td></tr>
              ) : currentApps.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-5 text-muted">No applications found.</td></tr>
              ) : (
                currentApps.map((app) => (
                  <tr key={app.application_id} className="app-row">
                    <td className="ps-4">
                      <div className="d-flex align-items-center">
                        <div className="position-relative">
                          {renderProfileImage(app)}
                          {(!app.profile_pic) && renderAvatarPlaceholder(app.full_name || 'U')}
                        </div>
                        <div className="ms-3">
                          <div className="fw-bold text-dark mb-0">{app.full_name}</div>
                          <div className="x-small text-muted">{app.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="fw-bold text-dark small">{app.title}</div>
                      <div className="x-small text-muted">{app.job_category}</div>
                    </td>
                    <td>
                      <div className="small fw-medium text-muted">
                        <FaBriefcase className="me-1 x-small" /> {app.company_name}
                      </div>
                    </td>
                    <td>
                      <div className="x-small text-muted d-flex align-items-center">
                        <FaCalendarAlt className="me-1" />
                        {moment(app.application_date).format('DD MMM YYYY')}
                      </div>
                    </td>
                    <td>
                      <Form.Select
                        value={app.job_status}
                        onChange={(e) => handleStatusChange(app.application_id, e.target.value)}
                        className="form-select-sm border-0 bg-light-subtle fw-bold small"
                        style={{ width: '130px' }}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Review">Review</option>
                        <option value="Accepted">Accepted</option>
                        <option value="Rejected">Rejected</option>
                      </Form.Select>
                    </td>
                    <td className="text-end pe-4">
                      <Button 
                        variant="light" 
                        size="sm" 
                        className="btn-icon text-primary shadow-none"
                        onClick={() => { setSelectedApp(app); setShowModal(true); }}
                      >
                        <FaEye />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="p-3 bg-light border-top d-flex justify-content-between align-items-center">
            <div className="small text-muted">Showing {indexOfFirstApp + 1} to {Math.min(indexOfLastApp, filteredApplications.length)} of {filteredApplications.length}</div>
            <Pagination className="mb-0 modern-pagination">
              <Pagination.Prev onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} />
              {renderPaginationItems()}
              <Pagination.Next onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} />
            </Pagination>
          </div>
        )}
      </Card>

      {/* Application Summary Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="app-detail-modal">
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div className="mb-2">{renderProfileImage(selectedApp, 70) || renderAvatarPlaceholder(selectedApp?.full_name || 'U', 70)}</div>
            <h5 className="fw-bold mb-0">{selectedApp?.full_name}</h5>
            <p className="text-muted small mb-0">{selectedApp?.email}</p>
          </div>
          <div className="bg-light p-3 rounded-3 mb-3">
            <Row className="g-2 text-center">
              <Col xs={6} className="border-end">
                <div className="x-small text-muted text-uppercase fw-bold">Position</div>
                <div className="small fw-bold text-dark">{selectedApp?.title}</div>
              </Col>
              <Col xs={6}>
                <div className="x-small text-muted text-uppercase fw-bold">Company</div>
                <div className="small fw-bold text-dark">{selectedApp?.company_name}</div>
              </Col>
            </Row>
          </div>
          <div className="d-grid gap-2">
            <Button variant="primary" className="fw-bold" onClick={() => handleProfileClick(selectedApp)}>VIEW FULL RESUME</Button>
            <Button variant="light" onClick={() => setShowModal(false)} className="text-muted fw-bold">CLOSE</Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Full Profile Modal */}
      <Modal show={showProfileModal} onHide={() => setShowProfileModal(false)} size="lg" centered className="profile-detail-modal">
        <Modal.Header closeButton className="border-0 pb-0" />
        <Modal.Body className="pt-0">
          {userProfileLoading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : userProfile ? (
            <div className="profile-wrapper px-3 pb-3">
              <div className="d-flex align-items-center mb-4 border-bottom pb-4">
                {renderProfileImage(userProfile.contact || userProfile, 100) || renderAvatarPlaceholder((userProfile.contact || userProfile).full_name, 100)}
                <div className="ms-4">
                  <h2 className="fw-bold mb-1">{(userProfile.contact || userProfile).full_name}</h2>
                  <div className="d-flex flex-wrap gap-3 text-muted small">
                    <span className="d-flex align-items-center"><FaEnvelope className="me-1 text-primary" /> {(userProfile.contact || userProfile).email}</span>
                    {(userProfile.contact || userProfile).location && (
                      <span className="d-flex align-items-center"><FaMapMarkerAlt className="me-1 text-danger" /> {(userProfile.contact || userProfile).location}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div className="section-label mb-2">Professional Summary</div>
                <div className="bg-light p-3 rounded-3 small line-height-relaxed">
                  {(userProfile.contact || userProfile).summary || 'Candidate has not provided a summary.'}
                </div>
              </div>

              <div className="mb-4">
                <div className="section-label mb-2">Skills</div>
                <div className="d-flex flex-wrap gap-2">
                  {userProfile.skills?.map(skill => (
                    <Badge key={skill.id} bg="primary-subtle" text="primary" className="px-3 py-2 fw-medium border-0">{skill.skill_name}</Badge>
                  ))}
                </div>
              </div>

              <div>
                <div className="section-label mb-2">Work History</div>
                <div className="timeline-minimal ps-3 border-start">
                  {userProfile.experience?.map(exp => (
                    <div key={exp.id} className="mb-4 position-relative">
                      <div className="fw-bold text-dark small">{exp.job_title}</div>
                      <div className="text-primary x-small fw-bold">{exp.company}</div>
                      <div className="text-muted x-small mb-2">{exp.start_date} - {exp.end_date || 'Present'}</div>
                      <div className="x-small text-muted">{exp.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : <div className="text-center py-4">No data</div>}
        </Modal.Body>
      </Modal>

      <style>{`
        .apps-mgmt-container {
          background: #f8fafc;
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
        }

        .metric-card {
          border-radius: 12px;
          transition: transform 0.2s ease;
        }

        .metric-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
        }

        .modern-table thead th {
          background: #f8fafc;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #94a3b8;
          border-top: 0;
          font-weight: 700;
          padding: 1rem !important;
        }

        .app-row:hover {
          background-color: #f1f5f9;
        }

        .btn-icon {
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border-radius: 8px;
        }

        .section-label {
          font-size: 0.7rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #94a3b8;
        }

        .x-small { font-size: 0.75rem; }
        .modern-pagination .page-link { border: 0; color: #64748b; font-weight: 600; border-radius: 6px; }
        .modern-pagination .page-item.active .page-link { background: #6366f1; color: white; }
        .app-detail-modal .modal-content, .profile-detail-modal .modal-content { border-radius: 1.5rem; border: 0; overflow: hidden; }

        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default JobApplications;